import { LocalNotifications } from '@capacitor/local-notifications';
import { Preferences } from '@capacitor/preferences';
import dayjs from 'dayjs';
import ActionTypes, { PREVENTION_JOURNEY_COUNT, PREVENTION_JOURNEY_IDS } from 'notifications';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { useTranslation } from 'react-i18next';
import Platform from 'utils/Platform';

/** Interval used for prevention-program A/B test variants (automatic and opt-in). */
export const PREVENTION_PROGRAM_INTERVAL_DAYS = 8;

export const NOTIFICATION_ARTICLES = [
  {
    id: 68,
    text: 'Consultation annuelle chez le gynécologue, frottis : pourquoi et à quelle fréquence ?',
  },
  {
    id: 67,
    text: 'Autopalpation, examens et mammographie : mode d’emploi',
  },
  {
    id: 72,
    text: 'Comment fonctionne le cycle menstruel ? Comment en faire votre allié ?',
  },
  {
    id: 73,
    text: 'Comprendre vos pertes vaginales pour comprendre votre corps',
  },
];

export default function useScheduleArticleNotifications() {
  const { t } = useTranslation();

  const useEnhancedNotifications =
    Platform.getPlatform() === 'ios' && posthog.getFeatureFlag('ios-notifications-better-settings') === true;

  type NotificationToSchedule = {
    title: string;
    body: string;
    id: number;
    schedule: { at: Date; allowWhileIdle: boolean };
    actionTypeId: string;
    sound?: string;
    extra?: {
      articleId: number;
    };
  };

  /**
   * Performs the initial schedule of article reminders (when none are pending yet).
   * Opt-in: 3 reminders (articles 2, 3, 4) at 8, 16, 24 days from baseDate.
   * Automatic: 4 reminders at 8, 16, 24, 32 days from now.
   * No-op if ARTICLES_READ_REMINDERS_INITIAL_SCHEDULED is already set.
   */
  const scheduleInitialArticleReminders = () => {
    LocalNotifications.checkPermissions().then(result => {
      if (result.display === 'granted') {
        Preferences.get({ key: Keys.PREVENTION_JOURNEY_STARTED }).then(result => {
          const parsed = JSON.parse(result?.value || '{}');
          if (parsed?.value) {
            return; // already did initial schedule and all are done
          }
          const isOptIn = posthog.getFeatureFlag('prevention-program') === 'opt-in';
          const count = isOptIn ? PREVENTION_JOURNEY_COUNT - 1 : PREVENTION_JOURNEY_COUNT;
          const startIndex = isOptIn ? 1 : 0;
          const notificationsToSchedule: NotificationToSchedule[] = Array.from({ length: count }, (_, i) => {
            const reminderIndex = startIndex + i;
            const daysFromToday = (i + 1) * PREVENTION_PROGRAM_INTERVAL_DAYS;
            return {
              title: t('appName'),
              body: NOTIFICATION_ARTICLES[reminderIndex].text,
              id: PREVENTION_JOURNEY_IDS[reminderIndex],
              schedule: {
                at: dayjs().add(daysFromToday, 'day').set('hour', 18).set('minute', 0).toDate(),
                allowWhileIdle: true,
              },
              actionTypeId: ActionTypes.PREVENTION_JOURNEY,
              extra: {
                articleId: NOTIFICATION_ARTICLES[reminderIndex].id,
              },
              ...(useEnhancedNotifications ? { sound: 'default' } : {}),
            };
          });

          LocalNotifications.schedule({ notifications: notificationsToSchedule }).then(() => {
            Preferences.set({
              key: Keys.PREVENTION_JOURNEY_STARTED,
              value: JSON.stringify({ value: true }),
            });
          });
        });
      }
    });
  };

  /**
   * Reschedules existing pending article reminders from now (app open).
   * Call when the user opens the app (automatic variant): undelivered reminders are moved to 8, 16, 24, … days from this app open.
   */
  const rescheduleArticleRemindersFromNow = () => {
    if (posthog.getFeatureFlag('prevention-program') !== 'automatic') {
      // for safety
      return;
    }

    LocalNotifications.getPending().then(pending => {
      const articlePending = pending.notifications.filter(n => PREVENTION_JOURNEY_IDS.includes(n.id));
      const pendingCount = articlePending.length;
      if (pendingCount === 0) return;

      const receivedCount = PREVENTION_JOURNEY_COUNT - pendingCount;
      const now = dayjs();
      const notificationsToSchedule: NotificationToSchedule[] = [];

      for (let i = 0; i < pendingCount; i += 1) {
        const reminderIndex = receivedCount + i;
        const daysFromNow = (i + 1) * PREVENTION_PROGRAM_INTERVAL_DAYS;
        const at = now.add(daysFromNow, 'day').set('hour', 18).set('minute', 0).toDate();
        notificationsToSchedule.push({
          title: t('appName'),
          body: NOTIFICATION_ARTICLES[reminderIndex].text,
          id: PREVENTION_JOURNEY_IDS[reminderIndex],
          schedule: { at, allowWhileIdle: true },
          actionTypeId: ActionTypes.PREVENTION_JOURNEY,
          extra: {
            articleId: NOTIFICATION_ARTICLES[reminderIndex].id,
          },
          ...(useEnhancedNotifications ? { sound: 'default' } : {}),
        });
      }

      LocalNotifications.cancel({ notifications: articlePending }).then(() => {
        LocalNotifications.schedule({ notifications: notificationsToSchedule });
      });
    });
  };

  return {
    scheduleInitialArticleReminders,
    rescheduleArticleRemindersFromNow,
  };
}
